import type { BriefBlock, BriefSection } from './briefMarkdown';

/**
 * From sections to cards: the shape Option B draws.
 *
 * Kyu, 11 September, from three mockups: "Option B", the iOS grouped look.
 * Grey ground, a small grey label for each ## section, one white card per
 * ### heading inside it, hairline rows inside the card. The label bullets
 * the writer uses ("**Pulse:**", "**What moved:**") become the rows.
 *
 * All of this is derived from the pasted text on every render. Nothing is
 * stored in this shape, so a brief written in a different shape still
 * comes out as cards, just plainer ones.
 */

export interface BriefRow {
  /** "Pulse", "What moved". Inline HTML with the bold and colon removed. */
  label: string | null;
  /** One paragraph of inline HTML, or null when the row is only bullets. */
  html: string | null;
  bullets: string[];
  numbered: boolean;
}

export interface BriefCard {
  /** The ### heading, inline HTML. Null for content that sits under a ##
   *  with no ### of its own. */
  title: string | null;
  /** A workstream's standing with Alex, read from its own "Needs Alex" row. */
  status: 'clear' | 'needs' | null;
  rows: BriefRow[];
}

export interface BriefGroup {
  /** The ## (or #) heading, inline HTML. Null above the first one. */
  label: string | null;
  /** The "Needs Alex" section, which the page draws in the brand blue. */
  tone: 'needs' | 'plain';
  cards: BriefCard[];
}

const LABEL_HTML = /^<strong>([^<]*?):?<\/strong>:?\s*(.*)$/s;
const NEEDS_ALEX = /^\s*needs\s+alex\s*:?\s*$/i;
const NOTHING = /\b(nothing|none|no asks?|n\/a)\b/i;

/** "<strong>Pulse:</strong> text" → ["Pulse", "text"]; anything else → null. */
export function splitLabel(html: string): [string, string] | null {
  const match = LABEL_HTML.exec(html);
  if (match === null) return null;
  return [(match[1] ?? '').trim(), (match[2] ?? '').trim()];
}

function plainText(html: string): string {
  return html.replace(/<[^>]+>/g, '');
}

/** One block into rows. A list becomes one row per label item, and one
 *  row for each run of plain items; a paragraph is a row of its own. */
export function rowsOf(block: BriefBlock): BriefRow[] {
  if (block.kind === 'p') {
    return [{ label: null, html: block.html[0] ?? '', bullets: [], numbered: false }];
  }
  if (block.kind !== 'ul' && block.kind !== 'ol') return [];
  const numbered = block.kind === 'ol';
  const items = block.items ?? block.html.map((html) => ({ html, children: [] }));
  const rows: BriefRow[] = [];
  let run: string[] = [];
  const flushRun = () => {
    if (run.length === 0) return;
    rows.push({ label: null, html: null, bullets: run, numbered });
    run = [];
  };
  for (const item of items) {
    const split = splitLabel(item.html);
    if (split === null && item.children.length === 0) {
      run.push(item.html);
      continue;
    }
    flushRun();
    if (split === null) {
      rows.push({ label: null, html: item.html, bullets: item.children, numbered: false });
    } else {
      rows.push({
        label: split[0],
        html: split[1] === '' ? null : split[1],
        bullets: item.children,
        numbered: false,
      });
    }
  }
  flushRun();
  return rows;
}

/** What the card's own "Needs Alex" row says, if it has one. */
export function cardStatus(rows: readonly BriefRow[]): BriefCard['status'] {
  const row = rows.find(
    (candidate) => candidate.label !== null && NEEDS_ALEX.test(plainText(candidate.label)),
  );
  if (row === undefined) return null;
  const text = [row.html ?? '', ...row.bullets].map(plainText).join(' ');
  return NOTHING.test(text) ? 'clear' : 'needs';
}

function cardOf(title: string | null, body: readonly BriefBlock[]): BriefCard {
  const rows = body.flatMap(rowsOf);
  return { title, status: title === null ? null : cardStatus(rows), rows };
}

function isSection(kind: BriefBlock['kind'] | null): boolean {
  return kind === null || kind === 'h1' || kind === 'h2';
}

export function buildBriefGroups(sections: readonly BriefSection[]): BriefGroup[] {
  const groups: BriefGroup[] = [];
  let open: BriefGroup | null = null;

  const push = (group: BriefGroup | null) => {
    if (group !== null && (group.label !== null || group.cards.length > 0)) groups.push(group);
  };

  for (const section of sections) {
    const heading = section.heading;
    const text = heading?.html[0] ?? null;
    if (isSection(heading?.kind ?? null)) {
      push(open);
      open = {
        label: text,
        tone: text !== null && NEEDS_ALEX.test(plainText(text)) ? 'needs' : 'plain',
        cards: [],
      };
      if (section.body.length > 0) open.cards.push(cardOf(null, section.body));
      continue;
    }
    if (open === null) open = { label: null, tone: 'plain', cards: [] };
    open.cards.push(cardOf(text, section.body));
  }
  push(open);
  return groups;
}
